/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage, Link, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import {
  CommunityType,
  getCommunityMemberCount,
  useActiveSidebarTab,
  useRequestCommunitiesAdminCommunity,
  useRequestCommunitiesChildren,
} from "@packages/sdk";
import type { CSSColorStrict, ThemeType } from "@packages/ui";
import {
  AvatarCommunity,
  ListProfile,
  spacing,
  TabGroup,
  TabGroupItem,
  useErrorNotification,
  useIntersectionObserverRefState,
  useSearchParamState,
} from "@packages/ui";
import { useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";

import { getBasePADUrl, padTabsPaths } from "../../../../lib";
import { EditGroupDropdownMenu } from "../EditGroupDropdownMenu";
import { GroupsEmptyState } from "../GroupsEmptyState";

const baseStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
};

const listProfilePlaceHolderStyles: Interpolation = {
  height: 40,
  width: 40,
};

const groupContainerStyles: Interpolation = {
  position: "relative",
};

const groupIconButtonStyles: Interpolation = {
  position: "absolute",
  right: 0,
  top: "50%",
  transform: "translate(-50%, -50%)",
};

const tabStyles: Interpolation = {
  justifyContent: "center",
  width: "100%",
};

const tabGroupStyles: Interpolation = {
  paddingBottom: spacing.s,
  paddingLeft: spacing.m,

  paddingRight: spacing.m,
};

const tabInactiveStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsLow,
});

const stylesLoading = {
  flex: {
    display: "flex",
  },
  hidden: {
    display: "none",
  },
};

const typeOptions: {
  message: string;
  value: "all" | "churches" | "groups";
}[] = [
  {
    message: "dashboard_date_filter_all",
    value: "all",
  },
  {
    message: "community_detail_churches_screen_title",
    value: "churches",
  },
  {
    message: "community_detail_groups_screen_title",
    value: "groups",
  },
];

type GroupsListProps = {
  communityId: number;
};

const GROUPS_LIST_TOTAL_LOADING_SKELETONS = 6;

export const GroupsList = ({ communityId }: GroupsListProps) => {
  const t = useTranslations();
  const analytics = useAnalytics();
  const searchParams = useSearchParams();

  const activeSidebarTab = useActiveSidebarTab({
    baseUrl: getBasePADUrl({ communityId }),
    tabs: padTabsPaths,
  });

  const [typeSearchParam, setTypeSearchParam] = useSearchParamState({
    defaultValue: "all",
    key: "type",
  });

  const infiniteScrollBufferRef = useRef<HTMLDivElement>(null);
  const [infiniteScrollBufferRefShowing] = useIntersectionObserverRefState({
    ref: infiniteScrollBufferRef,
  });

  const { query: queryCommunitiesAdminCommunity } =
    useRequestCommunitiesAdminCommunity({ id: communityId });

  const isNetwork =
    queryCommunitiesAdminCommunity.data?.type === CommunityType.network;

  const { infiniteQuery: communitiesChildrenBasicOrSmallGroupInfiniteQuery } =
    useRequestCommunitiesChildren({
      id: communityId,
      type: isNetwork ? CommunityType.basic : CommunityType.small_group,
    });
  const { infiniteQuery: communitiesChildrenParishInfiniteQuery } =
    useRequestCommunitiesChildren({
      enabled: isNetwork,
      id: communityId,
      type: CommunityType.parish,
    });

  const { query: parentCommunityQuery } = useRequestCommunitiesAdminCommunity({
    id: communityId,
  });

  useErrorNotification({
    isError:
      communitiesChildrenBasicOrSmallGroupInfiniteQuery.isError ||
      communitiesChildrenParishInfiniteQuery.isError,
  });

  const shouldFetchNextPagecommunitiesChildrenBasicOrSmallGroupInfiniteQuery =
    infiniteScrollBufferRefShowing &&
    communitiesChildrenBasicOrSmallGroupInfiniteQuery.hasNextPage &&
    !communitiesChildrenBasicOrSmallGroupInfiniteQuery.isFetchingNextPage;

  const shouldFetchNextPageCommunitiesChildrenParishInfiniteQuery =
    infiniteScrollBufferRefShowing &&
    communitiesChildrenParishInfiniteQuery.hasNextPage &&
    !communitiesChildrenParishInfiniteQuery.isFetchingNextPage;

  useEffect(() => {
    if (shouldFetchNextPagecommunitiesChildrenBasicOrSmallGroupInfiniteQuery)
      communitiesChildrenBasicOrSmallGroupInfiniteQuery.fetchNextPage();
  }, [shouldFetchNextPagecommunitiesChildrenBasicOrSmallGroupInfiniteQuery]);

  useEffect(() => {
    if (shouldFetchNextPageCommunitiesChildrenParishInfiniteQuery)
      communitiesChildrenParishInfiniteQuery.fetchNextPage();
  }, [shouldFetchNextPageCommunitiesChildrenParishInfiniteQuery]);

  const isShowingBasicOrSmallGroups = isNetwork
    ? ["all", "groups"].includes(typeSearchParam)
    : true;
  const isShowingParish = isNetwork
    ? ["all", "churches"].includes(typeSearchParam)
    : false;

  const childCommunities = [
    ...(isShowingBasicOrSmallGroups
      ? (communitiesChildrenBasicOrSmallGroupInfiniteQuery.data?.pages ?? [])
      : []),
    ...(isShowingParish
      ? (communitiesChildrenParishInfiniteQuery.data?.pages ?? [])
      : []),
  ].flatMap((page) => page.results);

  if (
    communitiesChildrenBasicOrSmallGroupInfiniteQuery.isLoading ||
    communitiesChildrenParishInfiniteQuery.isLoading
  )
    return (
      <div css={baseStyles}>
        {Array.from(Array(GROUPS_LIST_TOTAL_LOADING_SKELETONS).keys()).map(
          (i) => (
            <ListProfile key={`group-list-skeleton-${i}`} loading title="" />
          ),
        )}
      </div>
    );

  if (childCommunities.length === 0)
    return <GroupsEmptyState isNetwork={isNetwork} />;

  return (
    <>
      <div css={baseStyles}>
        {isNetwork && (
          <TabGroup
            setParentState={setTypeSearchParam}
            css={tabGroupStyles}
            value={typeSearchParam}
            type="single"
          >
            {typeOptions.map((typeOption) => (
              <TabGroupItem
                key={typeOption.value}
                parentState={typeSearchParam}
                css={[
                  tabStyles,
                  typeOption.value != typeSearchParam && tabInactiveStyles,
                ]}
                value={typeOption.value}
              >
                <I18nMessage message={typeOption.message} />
              </TabGroupItem>
            ))}
          </TabGroup>
        )}
        {childCommunities.map((childCommunity) => (
          <div
            key={`group-list-item-${childCommunity.id}`}
            css={groupContainerStyles}
          >
            <ListProfile
              asChild
              avatar={
                <AvatarCommunity
                  key={`avatar-${childCommunity.id}`}
                  imgSrc={childCommunity.image_url}
                  name={childCommunity.name}
                  color={childCommunity.color_hex as CSSColorStrict}
                  size="m"
                />
              }
              description={childCommunity.simple_address_label}
              icon={<div css={listProfilePlaceHolderStyles} />}
              title={childCommunity.name}
              metadata={t("create_community_content_member_count", {
                create_community_content_member_count: getCommunityMemberCount({
                  community: childCommunity,
                }),
              })}
              onClick={() => {
                analytics.track({
                  event: HallowAnalyticsEvent.TappedGroup,
                  properties: {
                    parent_group_type: parentCommunityQuery.data.type,
                    community_id: childCommunity.id,
                    location: activeSidebarTab ? `${activeSidebarTab}_tab` : "",
                    group_type: childCommunity.type,
                  },
                });
              }}
            >
              <Link
                href={`${getBasePADUrl({ communityId })}/groups/${childCommunity.id}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`}
              />
            </ListProfile>
            <EditGroupDropdownMenu
              communityId={communityId}
              childCommunity={childCommunity}
              triggerCSS={[groupIconButtonStyles]}
            />
          </div>
        ))}
      </div>
      <ListProfile
        loading
        ref={infiniteScrollBufferRef}
        css={[
          communitiesChildrenBasicOrSmallGroupInfiniteQuery.hasNextPage ||
          communitiesChildrenParishInfiniteQuery.hasNextPage
            ? stylesLoading.flex
            : stylesLoading.hidden,
        ]}
        title=""
      />
    </>
  );
};
